import { defineAsyncComponent } from '#imports';
import type { Component } from 'vue';
import type {
  BlockAdvantages,
  BlockAdvantagesVideo,
  BlockDifference,
  BlockImageTextLeft,
  BlockImageTextRight,
  BlockReview,
  BlockServiceCategory,
  BlockVideoReview,
  BockType,
  PageBlocks,
} from '~/types/pages';
import CTextBlock from '~/components/blocks/textBlock/CTextBlock.vue';
import CDocsBlock from '~/components/blocks/docsBlock/CDocsBlock.vue';
import CTextWithButton from '~/components/blocks/textWithButtonBlock/CTextWithButton.vue';
import COfficeBlock from '~/components/blocks/officeBlock/COfficeBlock.vue';
import CSliderImageTextBlock from '~/components/blocks/sliderImageTextBlock/CSliderImageTextBlock.vue';

const imageTextBlock = defineAsyncComponent(
  () => import('~/components/blocks/imageTextBlock/CImageTextBlock.vue'),
);
const spoilerBlock = defineAsyncComponent(
  () => import('~/components/blocks/spoilerBlock/CSpoilerBlock.vue'),
);
const sliderBlock = defineAsyncComponent(
  () => import('~/components/blocks/sliderBlock/CSliderBlock.vue'),
);
const imageBlock = defineAsyncComponent(
  () => import('~/components/blocks/imageBlock/CImageBlock.vue'),
);
const dynamicForm = defineAsyncComponent(
  () => import('~/components/blocks/simpleFormBlock/CSimpleFormBlock.vue'),
);

const subPagesBlock = defineAsyncComponent(
  () => import('~/components/blocks/subPagesBlock/CSubPagesBLock.vue'),
);

const bannerBlock = defineAsyncComponent(
  () => import('~/components/blocks/bannerBlock/CBannerBlock.vue'),
);
const reelsBlock = defineAsyncComponent(
  () => import('~/components/blocks/reelsBlock/CReelseBLock.vue'),
);

const ratingBlock = defineAsyncComponent(
  () => import('~/components/blocks/ratingBlock/CRatingBlock.vue'),
);
const afterBeforeSliderBlock = defineAsyncComponent(
  () => import('~/components/blocks/afterBeforeSlider/CAfterBeforeSlider.vue'),
);

const advantagesBlock = defineAsyncComponent(
  () => import('~/components/blocks/adventureBlock/CAdventureBlock.vue'),
);

const videoReviewBlock = defineAsyncComponent(
  () => import('~/components/blocks/videoReviewsBlock/CVideoReviewsBlock.vue'),
);

const reviewBlock = defineAsyncComponent(
  () => import('~/components/blocks/reviewBlock/CReviewBlock.vue'),
);

const textWithFormBlock = defineAsyncComponent(
  () => import('~/components/blocks/textWithForm/CTextWithForm.vue'),
);

const pricesBlock = defineAsyncComponent(
  () => import('~/components/blocks/pricesBlock/CPricesBlock.vue'),
);

const videoBlock = defineAsyncComponent(
  () => import('~/components/blocks/videoBlock/CVideoBlock.vue'),
);

const advantagesVideoBlock = defineAsyncComponent(
  () =>
    import('~/components/blocks/videoAdvantagesBlock/CVideoAdventagesBlock.vue'),
);

const certificatesBlock = defineAsyncComponent(
  () =>
    import(
      '~/components/blocks/certificateSliderBlock/CCertificateSliderBlock.vue'
    ),
);

const tabsBlock = defineAsyncComponent(
  () => import('~/components/blocks/tabsBlock/CBlockTabs.vue'),
);

const bannerMainPage = defineAsyncComponent(
  () => import('~/components/blocks/mainBannerPage/CBannerMainPage.vue'),
);

const serviceCatalogCategory = defineAsyncComponent(
  () =>
    import(
      '~/components/blocks/serviceCatalogCategoryBlock/CServiceCatalogCategoryBlock.vue'
    ),
);

const servicesCategoryBlock = defineAsyncComponent(
  () => import('~/components/blocks/serviceCategory/CServiceCategoryBlock.vue'),
);

const doctorsSliderBlock = defineAsyncComponent(
  () =>
    import('~/components/blocks/doctorCategoryBlock/CDoctorCategoryBlock.vue'),
);

const authorBlock = defineAsyncComponent(
  () => import('~/components/blocks/authorBlock/CAuthorBlock.vue'),
);

const videoTextForm = defineAsyncComponent(
  () => import('~/components/blocks/videoTextForm/CVideoTextForm.vue'),
);

const sliderAwards = defineAsyncComponent(
  () => import('~/components/blocks/sliderAwards/CSliderAwards.vue'),
);

export function useArticleBlocks() {
  const articleBlocks: Record<BockType, Component | undefined> = {
    BlockAuthor: authorBlock,
    BlockText: CTextBlock,
    BlockDocs: CDocsBlock,
    BlockImageTextLeft: imageTextBlock,
    BlockImageTextRight: imageTextBlock,
    BlockSpoiler: spoilerBlock,
    BlockSlider: sliderBlock,
    BlockImage: imageBlock,
    BlockDynamicForm: dynamicForm,
    BlockSubPages: subPagesBlock,
    BlockTextWithButton: CTextWithButton,
    BlockBanner: bannerBlock,
    BlockReels: reelsBlock,
    BlockSliderTextLeftRight: CSliderImageTextBlock,
    BlockRating: ratingBlock,
    BlockAdvantages: advantagesBlock,
    BlockDifference: afterBeforeSliderBlock,
    BlockVideoReview: videoReviewBlock,
    BlockReview: reviewBlock,
    BlockTextForm: textWithFormBlock,
    BlockPrice: pricesBlock,
    BlockVideo: videoBlock,
    BlockAdvantagesVideo: advantagesVideoBlock,
    BlockOffice: COfficeBlock,
    BlockSliderCertificates: certificatesBlock,
    BlockTabs: tabsBlock,
    BlockMainBanner: bannerMainPage,
    BlockServiceCatalogCategory: serviceCatalogCategory,
    BlockServiceCategory: servicesCategoryBlock,
    BlockDoctorCategory: doctorsSliderBlock,
    BlockVideoTextForm: videoTextForm,
    BlockSliderAwards: sliderAwards,
  };

  const getBlocksProps = (block: PageBlocks, index: number | undefined) => {
    const blockProps: Record<BockType, unknown> = {
      BlockAuthor: {
        id: block.id,
        doctor: block.doctor,
        button_text: block.button_text,
        button_trigger: block.button_trigger,
        form_title: block.form_title,
        created: block.created,
        updated: block.updated,
        verified: block.verified,
      },
      BlockText: {
        id: block.id,
        text: block.text,
        title: block.name,
      },
      BlockDocs: {
        id: block.id,
        files: block.files,
        title: block.name,
      },
      BlockImageTextLeft: {
        id: block.id,
        blockId: block.id,
        image: block.image,
        'image-seo': block.image_seo,
        text: block.text,
        title: block.name,
        isReverse: true,
        imageIsRounded: block.image_is_rounded,
        buttonText: block.button_text,
        buttonTrigger: block.button_trigger,
      },
      BlockImageTextRight: {
        id: block.id,
        blockId: block.id,
        image: block.image,
        'image-seo': block.image_seo,
        text: block.text,
        title: block.name,
        isReverse: false,
        imageIsRounded: block.image_is_rounded,
        buttonText: block.button_text,
        buttonTrigger: block.button_trigger,
      },
      BlockSpoiler: {
        id: block.id,
        texts: block.texts,
        title: block.name,
      },
      BlockSlider: {
        id: block.id,
        media: block.media,
        title: block.name,
      },
      BlockImage: {
        id: block.id,
        title: block.name,
        imageSeo: block.image_seo,
        image: block.image,
      },
      BlockDynamicForm: {
        id: block.id,
        title: block?.form_title || block.form_model?.title,
        formKey: block.form_model?.key,
        helpText: block.form_model?.help_text,
        fields: block.form_model?.fields,
      },
      BlockSubPages: {
        id: block.id,
        subpages: block.subpages,
        title: block.name,
      },
      BlockTextWithButton: {
        id: block.id,
        title: block.name,
        text: block.text,
        buttonText: block.button_text,
        buttonTrigger: block.button_trigger,
      },
      BlockBanner: {
        id: block.id,
        title: block.name,
        text: block.text,
        buttonText: block.button_text,
        image: block.image,
        imageSeo: block.imageSeo,
        buttonTrigger: block.button_trigger,
      },
      BlockReels: {
        id: block.id,
        title: block.name,
        media: block.media,
        isLong: false,
      },
      BlockRating: {
        id: block.id,
        name: block.name,
        text: block.text,
        media: block.media,
      },
      BlockSliderTextLeftRight: {
        id: block.id,
        title: block.name,
        textBlocks: block.media?.map((item: BlockImageTextLeft | BlockImageTextRight) => ({
          blockId: block.id,
          mediaId: item.id,
          title: item.name,
          image: item.image,
          imageSeo: item.image_seo,
          text: item.text,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          isReverse: item.image_position !== 'right',
          imageIsRounded: item.image_is_rounded,
          button: item.button_text,
          buttonTrigger: item.button_trigger,
        })),
      },
      BlockAdvantages: {
        id: block.id,
        title: block.name,
        previewText: block.preview_text,
        items: (block.media as BlockAdvantages['media'])?.map(item => ({
          title: item.name,
          text: item.text,
        })),
      },
      BlockDifference: {
        id: block.id,
        title: block.name,
        description: block.description,
        items: (block.media as BlockDifference['media'])?.map(item => ({
          image1: item.image_before,
          image2: item.image_after,
          image1Seo: {
            image_title: item.image_seo?.image_before_title,
            image_alt: item.image_seo?.image_before_alt,
          },
          image2Seo: {
            image_title: item.image_seo?.image_after_title,
            image_alt: item.image_seo?.image_after_alt,
          },
          service: item.service?.title,
          doctor: {
            image: item.doctor?.image,
            title: item.doctor?.title,
          },
          description: item.text,
        })),
      },
      BlockVideoReview: {
        id: block.id,
        title: block.name,
        first_not_display: block.first_not_display,
        reviews: (block.reviews as BlockVideoReview['reviews'])?.map(item => ({
          image: item.image,
          imageSeo: item.image_seo,
          previewVideo: item.video,
          fullVideoLink: item.link,
          title: item.title,
          descriptions: item.preview_text,
          text: item.text,
          review_media_orientation: item.review_media_orientation,
        })),
      },
      BlockReview: {
        id: block.id,
        title: block.name,
        reviews: (block.reviews as BlockReview['reviews'])?.map(item => ({
          image: item.image,
          imageSeo: item.image_seo,
          previewVideo: item.video,
          fullVideoLink: item.link,
          title: item.title,
          descriptions: item.preview_text,
          text: item.text,
          slug: item.full_slug,
          date: item.review_date,
        })),
      },
      BlockTextForm: {
        id: block.id,
        title: block.name,
        text: block.text,
        form: block.form_model,
        formTitle: block.form_title,
        background: block.background,
        isFirst: index === 0,
        image: block.image,
        imageSeo: block.image_seo,
      },
      BlockPrice: {
        id: block.id,
        title: block.name,
        text: block.text,
        prices: block.prices,
      },
      BlockVideo: {
        id: block.id,
        title: block.name,
        link: block.link,
        image: block.image,
        imageSeo: block.image_seo,
      },
      BlockAdvantagesVideo: {
        id: block.id,
        title: block.name,
        media: (block as BlockAdvantagesVideo).media?.map(item => ({
          link: item.link,
          image: item.image,
          imageSeo: item.image_seo,
          previewVideo: item.video,
          name: item.name,
          text: item.text,
        })),
      },
      BlockOffice: {
        id: block.id,
        title: block.title,
        offices: block.offices?.map(item => ({
          title: item.title,
          workTime: item.working_time,
          address: item.address,
          phone: item.phone,
          lat: item.latitude,
          lon: item.longitude,
          image: item.image,
          rating: item.blocks.find(el => el.resourcetype === 'OfficeBlockRating'),
          button_text: item.button_text,
          video_link: item.video_link
        })),
      },
      BlockSliderCertificates: {
        id: block.id,
        title: block.name,
        media: block.media,
      },
      BlockTabs: {
        id: block.id,
        title: block.name,
        tabs: block.media?.map(item => ({
          blockId: block.id,
          ...item,
        })),
      },
      BlockMainBanner: {
        id: block.id,
        title: block.name,
        image: block.image,
        imageSeo: block.image_seo,
        text: block.text,
        buttonText: block.button_text,
        buttonTrigger: block.button_trigger,
        buttonPhone: {
          text: block.button_text_phone,
          phone: block.phone,
        },
      },
      BlockServiceCatalogCategory: {
        id: block.id,
        title: block.name,
        categories: block.categories,
      },
      BlockServiceCategory: {
        id: block.id,
        title: block.name,
        services: (block.media as BlockServiceCategory['media'])?.map(el => ({
          image: el.image,
          imageSeo: el.image_seo,
          title: el.title,
          slug: el.full_slug,
        })),
      },
      BlockDoctorCategory: {
        id: block.id,
        title: block.name,
        doctors: block.doctors,
      },
      BlockVideoTextForm: {
        id: block.id,
        title: block.name,
        image: block.image,
        imageSeo: block.image_seo,
        video_title: block.video_title,
        previewVideo: block.video,
        link: block.link,
        text: block.text,
        formTitle: block.form_title,
        formModel: block.form_model,
      },
      BlockSliderAwards: {
        id: block.id,
        title: block.name,
        background: block.background,
        media: block.media,
      },

    };

    return blockProps[block.type || block.resourcetype];
  };

  return {
    articleBlocks,
    getBlocksProps,
  };
}
